"use client";

import {
  makeAssistantToolUI,
  useAui,
  useAuiState,
  type ToolCallMessagePartProps
} from "@assistant-ui/react";
import { CheckIcon, CircleHelpIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  countAnsweredQuestions,
  getQuestionDraft,
  useQuestionResponses
} from "@/agents-v2/questions/question-response-context";
import { createMetadataOnlyMessageContent } from "@/agents-v2/message-metadata/metadata-only-message";
import {
  AGENT_QUESTION_RESPONSES_METADATA_KEY,
  ASK_QUESTION_TOOL_NAME,
  buildQuestionResponseSet,
  draftAnswersFromResponse,
  emptyQuestionAnswer,
  getActiveQuestionToolCallIds,
  isLegacyQuestionCard,
  isQuestionAnswered,
  parseAskQuestionInterrupt,
  parseQuestionSet,
  parseQuestionResponseSets,
  parseQuestionToolOutcome,
  resolveQuestionLifecycle,
  toQuestionResumePayload,
  type QuestionDraftAnswer,
  type QuestionOption,
  type ThreadQuestion,
  type ThreadQuestionSet
} from "@/agents-v2/questions/question-responses";
import { useInterrupt } from "@/agents-v2/interrupts/use-interrupt";
import { Button } from "@/agents-v2/components/ui/button";
import { Input } from "@/agents-v2/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/agents-v2/components/ui/tooltip";
import { cn } from "@/agents-v2/utils/cn";

const answeredLabel = (count: number) =>
  `${count} ${count === 1 ? "answer" : "answers"}`;

export const QuestionOptionControl = ({
  disabled,
  index,
  multiSelect,
  onSelect,
  option,
  selected
}: {
  disabled: boolean;
  index: number;
  multiSelect: boolean;
  onSelect: () => void;
  option: QuestionOption;
  selected: boolean;
}) => {
  const control = (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      aria-label={
        multiSelect ? option.label : `${index + 1} ${option.label}`
      }
      onClick={onSelect}
      className={cn(
        "flex min-h-9 min-w-0 items-start gap-2 rounded-lg border-0 bg-transparent px-2.5 py-2 text-left text-base leading-relaxed transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-default disabled:opacity-100",
        selected ? "bg-orange-ultra_light text-foreground" : "text-foreground",
        // Only an option that can still be picked reacts to the pointer; a
        // submitted answer keeps its tint and the rest stay plain.
        !disabled && !selected && "hover:bg-accent hover:text-accent-foreground",
        disabled && !selected && "text-foreground/65"
      )}
    >
      {multiSelect ? (
        <span
          aria-hidden="true"
          className={cn(
            "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded",
            selected
              ? "bg-orange text-primary-foreground"
              : "border border-input text-muted-foreground"
          )}
        >
          {selected ? <CheckIcon className="size-3.5" /> : null}
        </span>
      ) : (
        <span
          className={cn(
            "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded text-xs tabular-nums",
            selected
              ? "bg-orange text-primary-foreground"
              : "text-foreground/70"
          )}
        >
          {index + 1}
        </span>
      )}
      <span className="line-clamp-5 min-w-0 flex-1 break-words sm:line-clamp-3">
        {option.label}
        {option.recommended ? (
          <span
            className={cn(
              "ml-2 inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 align-middle text-[11px] font-medium leading-4",
              selected
                ? "bg-orange text-primary-foreground"
                : "bg-muted text-muted-foreground"
            )}
          >
            Recommended
          </span>
        ) : null}
        {option.recommended && option.recommendationReason ? (
          <span className="ml-2 text-sm text-muted-foreground">
            {option.recommendationReason}
          </span>
        ) : null}
      </span>
    </button>
  );

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>{control}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-64">
          {option.tooltip ?? option.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * The "your own answer" option: the chip shape of every other option, but with
 * a Tt box instead of a number and a text field in place of the label, so it is
 * ready to type into rather than something to click first.
 * @param root0 - The component props.
 * @param root0.disabled - Whether the question set is locked.
 * @param root0.label - Accessible label for the text field.
 * @param root0.onChange - Called with the typed value.
 * @param root0.option - The option this field stands in for.
 * @param root0.selected - Whether this option is currently chosen.
 * @param root0.value - The current typed value.
 * @returns The rendered free-text option row.
 */
export const QuestionFreeTextOption = ({
  disabled,
  label,
  onChange,
  option,
  selected,
  value
}: {
  disabled: boolean;
  label: string;
  onChange: (value: string) => void;
  option: QuestionOption;
  selected: boolean;
  value: string;
}) => (
  <div
    className={cn(
      "flex min-h-9 min-w-0 items-center gap-2 rounded-lg px-2.5 py-2 transition-colors",
      selected ? "bg-orange-ultra_light" : "bg-transparent"
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold leading-none",
        selected
          ? "bg-orange text-primary-foreground"
          : "border border-input text-foreground/70"
      )}
    >
      Tt
    </span>
    <input
      type="text"
      value={value}
      disabled={disabled}
      maxLength={1000}
      placeholder={option.clarificationPlaceholder ?? option.label}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
      className="min-w-0 flex-1 bg-transparent text-base leading-relaxed text-foreground outline-none placeholder:text-muted-foreground focus:outline-none focus-visible:outline-none disabled:cursor-default disabled:opacity-70"
    />
  </div>
);

export const QuestionItem = ({
  answer,
  disabled,
  index,
  onChange,
  question
}: {
  answer: QuestionDraftAnswer;
  disabled: boolean;
  index: number;
  onChange: (answer: QuestionDraftAnswer) => void;
  question: ThreadQuestion;
}) => {
  const handleOptionSelect = (option: QuestionOption) => {
    if (disabled) return;
    const isSelected = answer.selectedOptions.includes(option.label);
    const selectedOptions =
      question.answerType === "multi_select"
        ? isSelected
          ? answer.selectedOptions.filter((label) => label !== option.label)
          : [...answer.selectedOptions, option.label]
        : isSelected
          ? []
          : [option.label];
    const clarifications = { ...answer.clarifications };
    for (const label of Object.keys(clarifications)) {
      if (!selectedOptions.includes(label)) delete clarifications[label];
    }
    if (option.requiresClarification && !isSelected) {
      clarifications[option.label] ??= "";
    }
    onChange({ ...answer, selectedOptions, clarifications });
  };

  // The free-text box is always open, so typing in it is what picks that
  // option (and clearing it unpicks) - there is no chip to click first.
  const handleClarificationChange = (option: QuestionOption, value: string) => {
    if (disabled) return;
    const isSelected = answer.selectedOptions.includes(option.label);
    const hasText = value.trim() !== "";
    let selectedOptions = answer.selectedOptions;
    if (hasText && !isSelected) {
      selectedOptions =
        question.answerType === "multi_select"
          ? [...selectedOptions, option.label]
          : [option.label];
    } else if (!hasText && isSelected) {
      selectedOptions = selectedOptions.filter(
        (label) => label !== option.label
      );
    }
    const clarifications = { ...answer.clarifications };
    for (const label of Object.keys(clarifications)) {
      if (label !== option.label && !selectedOptions.includes(label)) {
        delete clarifications[label];
      }
    }
    clarifications[option.label] = value;
    onChange({ ...answer, selectedOptions, clarifications });
  };

  // The free-text option always sits at the bottom, so the picks read first and
  // the numbering runs unbroken down the chips above it.
  const orderedOptions = [
    ...question.options.filter((option) => !option.requiresClarification),
    ...question.options.filter((option) => option.requiresClarification)
  ];

  return (
    <div className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-2.5 gap-y-2">
      <span className="pt-0.5 text-right text-sm text-foreground/70 tabular-nums">
        {index + 1}
      </span>
      <p
        className={cn(
          "text-base font-medium leading-relaxed",
          disabled && "text-foreground/65"
        )}
      >
        {question.question}
      </p>

      <div />
      <div className="min-w-0 space-y-2">
        {question.answerType === "text" ? (
          <Input
            value={answer.text}
            disabled={disabled}
            maxLength={4000}
            placeholder={question.placeholder ?? "Type your answer..."}
            aria-label={question.question}
            onChange={(event) =>
              onChange({ ...answer, text: event.target.value })
            }
            className="h-10 rounded-lg border-0 bg-transparent text-base leading-relaxed shadow-none outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 md:text-base disabled:cursor-default disabled:opacity-70"
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            {orderedOptions.map((option, optionIndex) =>
              option.requiresClarification ? (
                <QuestionFreeTextOption
                  key={option.label}
                  disabled={disabled}
                  label={`${option.label}: ${question.question}`}
                  onChange={(value) => handleClarificationChange(option, value)}
                  option={option}
                  selected={answer.selectedOptions.includes(option.label)}
                  value={answer.clarifications[option.label] ?? ""}
                />
              ) : (
                <QuestionOptionControl
                  key={option.label}
                  disabled={disabled}
                  index={optionIndex}
                  multiSelect={question.answerType === "multi_select"}
                  onSelect={() => handleOptionSelect(option)}
                  option={option}
                  selected={answer.selectedOptions.includes(option.label)}
                />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const LockedStatus = ({
  answeredCount,
  questionCount
}: {
  answeredCount: number;
  questionCount: number;
}) => {
  const label =
    answeredCount === 0
      ? "Skipped"
      : answeredCount === questionCount
        ? "Submitted"
        : `Submitted ${answeredLabel(answeredCount)}`;

  return (
    <div className="flex justify-end pt-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled
        className="h-8 gap-1.5 px-3 text-xs"
      >
        {answeredCount > 0 ? <CheckIcon className="size-3.5" /> : null}
        {label}
      </Button>
    </div>
  );
};

const QuestionForm = ({
  questionSet,
  toolCallId
}: {
  questionSet: ThreadQuestionSet;
  toolCallId: string;
}) => {
  const aui = useAui();
  const threadId =
    useAuiState((state) => state.threadListItem.externalId) ?? "";
  const { drafts, submitDraft, updateAnswer } = useQuestionResponses();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const lifecycleJson = useAuiState((state) =>
    JSON.stringify(resolveQuestionLifecycle(state.thread.messages, toolCallId))
  );
  const lifecycle = useMemo(
    () =>
      JSON.parse(lifecycleJson) as ReturnType<typeof resolveQuestionLifecycle>,
    [lifecycleJson]
  );
  // The run pauses on the question, so answering it is a resume rather than a
  // new message. Only the card the run is actually waiting on may resume.
  const pendingInterrupt = useInterrupt(parseAskQuestionInterrupt);
  const resumeThisCard =
    pendingInterrupt?.request.toolCallId === toolCallId
      ? pendingInterrupt.resume
      : null;
  // Cards asked before the interrupt landed still answer the old way: their
  // responses ride on the next human message's metadata.
  const answersTravelOnNextMessage = useAuiState((state) =>
    isLegacyQuestionCard(state.thread.messages, toolCallId)
  );

  if (!threadId) return null;

  const draft = getQuestionDraft(drafts, threadId, toolCallId);
  const persistedAnswers = lifecycle.persistedResponse
    ? draftAnswersFromResponse(questionSet, lifecycle.persistedResponse)
    : null;
  const submittedDraft = draft?.phase === "submitted" ? draft.answers : null;
  const answers =
    persistedAnswers ??
    (lifecycle.locked ? submittedDraft : draft?.answers) ??
    questionSet.questions.map(emptyQuestionAnswer);
  const answeredCount = questionSet.questions.filter((question, index) =>
    isQuestionAnswered(question, answers[index])
  ).length;

  const canSubmit = Boolean(resumeThisCard) || answersTravelOnNextMessage;

  const submit = () => {
    if (answeredCount === 0 || lifecycle.locked || submittingRef.current) return;
    const response = buildQuestionResponseSet(toolCallId, questionSet, answers);
    if (!response) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    if (resumeThisCard) {
      // The answers become the tool's result, so the draft must not also be
      // attached to whatever the user types next.
      submitDraft(threadId, toolCallId);
      resumeThisCard(toQuestionResumePayload(toolCallId, response));
    } else {
      aui.thread().append({
        role: "user",
        content: createMetadataOnlyMessageContent(),
        metadata: {
          custom: { [AGENT_QUESTION_RESPONSES_METADATA_KEY]: [response] }
        }
      });
    }
  };

  return (
    <div
      data-slot="aui_question-response"
      className={cn("mt-3", lifecycle.locked && "text-foreground/65")}
    >
      <div className="mb-3">
        <p
          className={cn(
            "text-base font-medium leading-relaxed text-foreground",
            lifecycle.locked && "text-foreground/65"
          )}
        >
          {questionSet.title}
        </p>
      </div>

      <div className="space-y-5">
        {questionSet.questions.map((question, index) => (
          <QuestionItem
            key={`${index}:${question.question}`}
            answer={answers[index] ?? emptyQuestionAnswer()}
            disabled={lifecycle.locked}
            index={index}
            question={question}
            onChange={(answer) =>
              updateAnswer(threadId, toolCallId, questionSet, index, answer)
            }
          />
        ))}
      </div>

      {lifecycle.locked ? (
        <LockedStatus
          answeredCount={answeredCount}
          questionCount={questionSet.questions.length}
        />
      ) : canSubmit ? (
        <div className="flex justify-end pt-3">
          <Button
            type="button"
            size="sm"
            disabled={answeredCount === 0 || isSubmitting}
            onClick={submit}
            className="h-8 gap-1.5 px-3 text-xs"
          >
            Submit {answeredLabel(answeredCount)}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export const AskQuestionRender = ({
  args,
  argsText,
  isError,
  result,
  status,
  toolCallId
}: ToolCallMessagePartProps<unknown, unknown>) => {
  const questionSet = useMemo(
    () => parseQuestionSet(result) ?? parseQuestionSet(args),
    [args, result]
  );
  const outcome = useMemo(() => parseQuestionToolOutcome(result), [result]);

  // The card draws as soon as the agent has finished writing the question,
  // which is what the arguments being complete JSON tells us. Do NOT gate on
  // the run's status instead: a tool call with no result inherits its
  // message's status, so it reads "running" for the whole gap between the
  // model finishing the message and the tool actually executing. Middleware
  // can hold the run there for minutes, and waiting it out is the exact delay
  // this card was changed to avoid (PE-474).
  const argsReady = useMemo(() => {
    if (result !== undefined) return true;
    if (typeof argsText !== "string") return true;
    if (!argsText.trim()) return false;
    try {
      JSON.parse(argsText);
      return true;
    } catch {
      // Still streaming: drawing truncated arguments would flash a card
      // carrying only the options that had arrived so far.
      return false;
    }
  }, [argsText, result]);

  // A call the tool refused because it was bundled with other calls never
  // reached the user; the model asks again on its own.
  if (
    isError ||
    status.type === "incomplete" ||
    !argsReady ||
    !questionSet ||
    outcome?.status === "not_asked"
  ) {
    return null;
  }

  return <QuestionForm questionSet={questionSet} toolCallId={toolCallId} />;
};

export const AskQuestionToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: ASK_QUESTION_TOOL_NAME,
  render: AskQuestionRender
});

export const QuestionResponseSynchronizer = () => {
  const threadId = useAuiState((state) => state.threadListItem.externalId);
  const activeIdsJson = useAuiState((state) =>
    JSON.stringify(getActiveQuestionToolCallIds(state.thread.messages))
  );
  const { setActiveToolCallIds } = useQuestionResponses();

  useEffect(() => {
    if (!threadId) return;
    setActiveToolCallIds(threadId, JSON.parse(activeIdsJson) as string[]);
  }, [activeIdsJson, setActiveToolCallIds, threadId]);
  return null;
};

export const usePendingQuestionAnswerCount = () => {
  const threadId =
    useAuiState((state) => state.threadListItem.externalId) ?? "";
  const activeIdsJson = useAuiState((state) =>
    JSON.stringify(getActiveQuestionToolCallIds(state.thread.messages))
  );
  const { drafts } = useQuestionResponses();
  const activeIds = useMemo(
    () => JSON.parse(activeIdsJson) as string[],
    [activeIdsJson]
  );
  const answerCount = activeIds.reduce(
    (count, toolCallId) =>
      count +
      countAnsweredQuestions(getQuestionDraft(drafts, threadId, toolCallId)),
    0
  );

  return answerCount;
};

export const QuestionResponseChip = ({ answerCount }: { answerCount: number }) => (
  <div className="aui-question-response-chip inline-flex w-64 max-w-[70vw] shrink-0 items-center gap-2 rounded-lg border border-border bg-muted/50 px-2.5 py-1.5 text-sm">
    <span className="flex size-8 shrink-0 items-center justify-center rounded border border-border bg-background text-muted-foreground">
      <CircleHelpIcon className="size-4" aria-hidden="true" />
    </span>
    <div className="min-w-0 flex-1">
      <div className="truncate font-medium leading-5">
        {answeredLabel(answerCount)}
      </div>
      <div className="text-xs leading-4 text-muted-foreground">
        Question response
      </div>
    </div>
  </div>
);

export const useUserMessageQuestionAnswerCount = () => {
  const threadId =
    useAuiState((state) => state.threadListItem.externalId) ?? "";
  const responsesJson = useAuiState((state) =>
    JSON.stringify(
      state.message.metadata?.custom?.[
        AGENT_QUESTION_RESPONSES_METADATA_KEY
      ] ?? null
    )
  );
  const precedingQuestionIdsJson = useAuiState((state) => {
    const messageIndex = state.thread.messages.findIndex(
      (message) => message.id === state.message.id
    );
    return JSON.stringify(
      getActiveQuestionToolCallIds(
        messageIndex < 0
          ? []
          : state.thread.messages.slice(0, messageIndex)
      )
    );
  });
  const { drafts } = useQuestionResponses();

  const persistedAnswerCount = useMemo(() => {
    let responses: unknown = null;
    try {
      responses = JSON.parse(responsesJson);
    } catch {
      responses = null;
    }
    return parseQuestionResponseSets(responses).reduce(
      (count, response) => count + response.answers.length,
      0
    );
  }, [responsesJson]);

  return useMemo(() => {
    if (persistedAnswerCount > 0) return persistedAnswerCount;

    const toolCallIds = JSON.parse(precedingQuestionIdsJson) as string[];
    return toolCallIds.reduce(
      (count, toolCallId) =>
        count +
        countAnsweredQuestions(getQuestionDraft(drafts, threadId, toolCallId)),
      0
    );
  }, [drafts, persistedAnswerCount, precedingQuestionIdsJson, threadId]);
};

export const QuestionComposerIndicator = () => {
  const answerCount = usePendingQuestionAnswerCount();

  if (answerCount === 0) return null;
  return (
    <QuestionResponseChip answerCount={answerCount} />
  );
};
